import { useQueryClient } from '@tanstack/react-query'
import { Link, Outlet, createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import {
  Building2,
  BookOpen,
  CalendarClock,
  CalendarRange,
  BellRing,
  BookA,
  Coins,
  FileQuestion,
  FileSpreadsheet,
  GraduationCap,
  Landmark,
  Layers,
  ListChecks,
  LogOut,
  ShieldCheck,
  Tags,
  TriangleAlert,
  Trophy,
  UserCog,
  Users,
} from 'lucide-react'
import { getV1MeRetrieveQueryOptions, useV1MeRetrieve } from '@/api/client'
import type { RoleEnum } from '@/api/model'
import { BrandLogo } from '@/components/brand-logo'
import { BugReportTrigger } from '@/components/bug-report/bug-report-trigger'
import { PendingTasksTray } from '@/components/pending-tasks-tray'
import { AvatarPortrait } from '@/components/profile/dicebear-avatar'
import { SessionTimeoutWidget } from '@/components/session-timeout-widget'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { isRouteUnlocked, sectionOf } from '@/config/soft-launch'
import { authApi } from '@/lib/auth-api'
import { sessionQuery } from '@/lib/auth'
import { avatarSeed } from '@/lib/avatar-catalog'
import { panelAccessRedirect, resetRejectedMeSession } from '@/lib/me-route-access'
import { useIdleLogout } from '@/lib/use-idle-logout'

export const Route = createFileRoute('/_panel')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(sessionQuery)
    if (!session.isAuthenticated) {
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
    // TR-122/TR-200: uczeń nie korzysta z panelu administracyjnego.
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    const accessRedirect = panelAccessRedirect(me)
    if (accessRedirect === '/login') {
      resetRejectedMeSession(context.queryClient)
      throw redirect({ to: '/login', search: { redirect: location.href } })
    }
    if (accessRedirect === '/uczen') {
      throw redirect({ to: '/uczen' })
    }
    // Soft-launch: trasy spoza odblokowanego zbioru są nieosiągalne (także po
    // ręcznym wpisaniu URL). Egzekwujemy to w jednym miejscu dla wszystkich podtras.
    const section = sectionOf(location.pathname)
    if (section !== '/' && !isRouteUnlocked(section)) {
      throw redirect({ to: '/szkoly' })
    }
  },
  component: PanelLayout,
})

const nav: { to: string; icon: typeof Building2; label: string; roles: RoleEnum[] }[] = [
  { to: '/szkoly', icon: Building2, label: 'Szkoły', roles: ['superadmin', 'admin'] },
  { to: '/katalog-rspo', icon: Landmark, label: 'Katalog RSPO', roles: ['superadmin', 'admin'] },
  // Teacher-only surface (TR-501 R10): propose changes to assigned schools' data.
  { to: '/moje-szkoly', icon: Building2, label: 'Moje szkoły', roles: ['teacher'] },
  // Teacher-scoped class visibility is deferred (spec 012 DR-002): without a
  // teacher↔school link a teacher would see every school's classes, so the list
  // stays admin/superadmin-only until scoping lands.
  { to: '/klasy', icon: Users, label: 'Klasy', roles: ['superadmin', 'admin', 'teacher'] },
  { to: '/nauczyciele', icon: GraduationCap, label: 'Nauczyciele', roles: ['superadmin', 'admin'] },
  // Module config is an operator area (TR-1200 R6/R12): superadmin edits, admin
  // views read-only, teachers have no access at all — the entry must not show.
  { to: '/moduly', icon: Layers, label: 'Moduły', roles: ['superadmin', 'admin'] },
  {
    to: '/baza-wiedzy',
    icon: BookOpen,
    label: 'Baza wiedzy Finka',
    roles: ['superadmin', 'admin'],
  },
  { to: '/rankingi', icon: Trophy, label: 'Rankingi', roles: ['superadmin', 'admin', 'teacher'] },
  {
    to: '/punktacja',
    icon: Coins,
    label: 'Punktacja i nagrody',
    roles: ['superadmin', 'admin'],
  },
  // Shared authoring source for automatic and operator-distributed quizzes.
  {
    to: '/pytania',
    icon: FileQuestion,
    label: 'Bank pytań',
    roles: ['superadmin', 'admin'],
  },
  // Słownik tematów jest częścią banku pytań — ta sama rola, sąsiedni wpis.
  {
    to: '/tematy',
    icon: Tags,
    label: 'Słownik tematów',
    roles: ['superadmin', 'admin'],
  },
  {
    to: '/quizy-automatyczne',
    icon: CalendarClock,
    label: 'Kalendarz quizów',
    roles: ['superadmin', 'admin'],
  },
  // Plan nauczania czyta admin, zapisuje wyłącznie superadmin (backend:
  // `CurriculumAdminReadView` vs `CurriculumAdminWriteView`) — wpis widoczny
  // dla obu, bo sam podgląd gotowości do publikacji jest pracą admina.
  {
    to: '/plan-nauczania',
    icon: CalendarRange,
    label: 'Plan nauczania',
    roles: ['superadmin', 'admin'],
  },
  // Push quizzes (004): everything the area shows in v1 is operator-only, so the
  // entry narrows to admin + superadmin — a teacher must not see a nav item that
  // only bounces. Widen again when TR-1401/1402 ship a teacher-facing task type.
  {
    to: '/zadania',
    icon: ListChecks,
    label: 'Zadania aktywizujące',
    roles: ['superadmin', 'admin'],
  },
  {
    to: '/konta-administracyjne',
    icon: ShieldCheck,
    label: 'Konta administracyjne',
    roles: ['superadmin'],
  },
  {
    to: '/powiadomienia',
    icon: BellRing,
    label: 'Powiadomienia',
    roles: ['superadmin'],
  },
  { to: '/slowniki', icon: BookA, label: 'Słowniki', roles: ['superadmin'] },
  {
    to: '/eksport-danych',
    icon: FileSpreadsheet,
    label: 'Raporty danych uczniów',
    roles: ['superadmin'],
  },
  {
    to: '/bledy-systemowe',
    icon: TriangleAlert,
    label: 'Błędy systemowe',
    roles: ['superadmin', 'admin'],
  },
  { to: '/profil', icon: UserCog, label: 'Mój profil', roles: ['superadmin', 'admin', 'teacher'] },
]

const roleLabels: Record<RoleEnum, string> = {
  superadmin: 'superadministrator',
  admin: 'administrator',
  teacher: 'nauczyciel',
  student: 'uczeń',
}

function PanelLayout() {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { data: me } = useV1MeRetrieve()
  // The regenerated client types the response as a 200/401 union — narrow by status.
  const user = me?.status === 200 ? me.data : undefined

  // StaffMe carries `email`; StudentMe does not. The panel only ever renders
  // staff (students are redirected in beforeLoad), so email is the name fallback.
  const email = user && 'email' in user ? user.email : undefined
  const displayName = user ? user.nickname || email || '…' : '…'

  const handleLogout = async () => {
    await authApi.logout()
    queryClient.clear()
    router.history.push('/login')
  }

  // TR-124 R9/R10: krótka sesja z widgetem dotyczy nauczyciela.
  const idle = useIdleLogout(user?.role === 'teacher', '/login')
  const visibleNav = nav.filter((item) => !user || item.roles.includes(user.role))

  return (
    <div className="flex min-h-screen">
      {/* Pinned to the viewport so the account footer (pending-tasks bell) stays visible on long pages. */}
      <aside className="sticky top-0 flex h-svh w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <BrandLogo />
          <div>
            <p className="text-sm leading-none font-semibold">FinPath</p>
            <p className="mt-0.5 text-xs text-muted-foreground">panel administracyjny</p>
          </div>
        </div>
        <TooltipProvider>
          <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto px-3">
            {visibleNav.map(({ to, icon: Icon, label }) => {
              const itemClass =
                'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground'
              if (isRouteUnlocked(to)) {
                return (
                  <Link
                    key={to}
                    to={to}
                    className={`${itemClass} transition-colors hover:bg-muted hover:text-foreground`}
                    activeProps={{ className: 'bg-sidebar-accent text-sidebar-accent-foreground' }}
                  >
                    <Icon className="size-4" />
                    {label}
                  </Link>
                )
              }
              // Soft-launch: funkcja jeszcze niedostępna — pozycja widoczna, ale
              // wyszarzona i nieklikalna (brak nawigacji), z podpowiedzią „Wkrótce".
              return (
                <Tooltip key={to}>
                  <TooltipTrigger asChild>
                    <span
                      aria-disabled="true"
                      className={`${itemClass} cursor-not-allowed opacity-50 select-none`}
                    >
                      <Icon className="size-4" />
                      {label}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="right">Wkrótce</TooltipContent>
                </Tooltip>
              )
            })}
          </nav>
        </TooltipProvider>
        <div className="border-t px-3 py-3">
          <div className="flex items-center gap-2.5 px-2">
            {/* alt pusty: nazwa konta stoi tuż obok, więc obraz jest dekoracyjny. */}
            <AvatarPortrait
              options={user?.avatar}
              seed={avatarSeed(email ?? 'loading')}
              alt=""
              size="md"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm leading-none font-medium">{displayName}</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {user ? roleLabels[user.role] : ''}
              </p>
            </div>
            <PendingTasksTray />
            <Button variant="ghost" size="icon" title="Wyloguj" onClick={handleLogout}>
              <LogOut className="size-4" />
            </Button>
          </div>
        </div>
      </aside>
      <main className="min-w-0 flex-1 pb-16">
        {/* TR-100 R9: zachęta do utrzymywania >=2 aktywnych superadminów. */}
        {user?.role === 'superadmin' && (user.active_superadmin_count ?? 2) < 2 && (
          <div className="border-b border-amber-300 bg-amber-50 px-6 py-2.5 text-sm text-amber-900">
            W systemie jest tylko jedno aktywne konto superadministratora. Utwórz drugie, aby
            możliwe było odzyskanie dostępu (np. reset weryfikacji dwuetapowej).
          </div>
        )}
        <Outlet />
      </main>
      <SessionTimeoutWidget secondsLeft={idle.secondsLeft} onExtend={idle.extend} />
      <BugReportTrigger />
    </div>
  )
}
